import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { EmptyState } from '@/components/ui/shadcn/empty-state';
import { Input } from '@/components/ui/shadcn/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { type BreadcrumbItem, type PageProps } from '@/types';
import { Head, router } from '@inertiajs/react';
import { formatDistanceToNow } from 'date-fns';
import { Check, Copy, Lock, MonitorSmartphone, Pencil, RefreshCw, RotateCw, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

interface RemoteDeviceItem {
    id: number;
    hostname: string;
    rustdesk_id: string;
    last_seen_at: string | null;
    online: boolean;
    assigned_user_name: string | null;
    assigned_user_upn: string | null;
    name_overridden: boolean;
}

interface RemoteDevicesIndexProps extends PageProps {
    devices: RemoteDeviceItem[];
    onlineThresholdMinutes: number;
    intuneSyncEnabled: boolean;
    [key: string]: unknown;
}

const POLL_INTERVAL_MS = 60_000;

export default function RemoteDevicesIndex({ devices, onlineThresholdMinutes, intuneSyncEnabled }: RemoteDevicesIndexProps) {
    const { t } = useTranslations();
    const [search, setSearch] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editValue, setEditValue] = useState('');
    const [savingId, setSavingId] = useState<number | null>(null);
    const [syncingId, setSyncingId] = useState<number | null>(null);

    const breadcrumbs: BreadcrumbItem[] = [{ title: t('remote_devices.breadcrumb', 'Remote Devices'), href: '/remote-devices' }];

    // Poll the same route so online status stays fresh without a full reload.
    useEffect(() => {
        const id = window.setInterval(() => {
            router.reload({ only: ['devices'] });
        }, POLL_INTERVAL_MS);

        return () => window.clearInterval(id);
    }, []);

    const visibleDevices = useMemo(() => {
        const term = search.trim().toLowerCase();

        return devices
            .filter((device) => (term === '' ? true : device.hostname.toLowerCase().includes(term)))
            .sort((a, b) => {
                // Online first, then alphabetical by hostname.
                if (a.online !== b.online) {
                    return a.online ? -1 : 1;
                }
                return a.hostname.localeCompare(b.hostname);
            });
    }, [devices, search]);

    const onlineCount = useMemo(() => devices.filter((device) => device.online).length, [devices]);

    const copyId = async (device: RemoteDeviceItem) => {
        try {
            await navigator.clipboard.writeText(device.rustdesk_id);
            toast.success(t('remote_devices.copied', 'RustDesk ID copied'));
        } catch {
            toast.error(t('remote_devices.copy_failed', 'Could not copy to clipboard'));
        }
    };

    const startEdit = (device: RemoteDeviceItem) => {
        setEditingId(device.id);
        setEditValue(device.assigned_user_name ?? '');
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditValue('');
    };

    const saveName = (device: RemoteDeviceItem) => {
        const name = editValue.trim();
        if (name === '') {
            return;
        }
        setSavingId(device.id);
        router.patch(
            `/remote-devices/${device.id}`,
            { name },
            {
                preserveScroll: true,
                preserveState: true,
                onSuccess: () => cancelEdit(),
                onFinish: () => setSavingId(null),
            },
        );
    };

    // Force a single device back into sync with Intune, dropping its manual name.
    const syncDevice = (device: RemoteDeviceItem) => {
        setSyncingId(device.id);
        router.post(
            `/remote-devices/${device.id}/sync`,
            {},
            {
                preserveScroll: true,
                preserveState: true,
                onFinish: () => setSyncingId(null),
            },
        );
    };

    const lastSeenLabel = (device: RemoteDeviceItem) => {
        if (!device.last_seen_at) {
            return t('remote_devices.never_seen', 'Never');
        }
        return formatDistanceToNow(new Date(device.last_seen_at), { addSuffix: true });
    };

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('remote_devices.title', 'Remote Devices')} />

            <PageHeader
                title={t('remote_devices.title', 'Remote Devices')}
                subtitle={t('remote_devices.subtitle', 'Connect to enrolled Windows devices via RustDesk')}
                actions={
                    <Button variant="outline" size="sm" onClick={() => router.reload({ only: ['devices'] })}>
                        <RefreshCw className="h-4 w-4" />
                        <span className="hidden sm:inline">{t('remote_devices.refresh', 'Refresh')}</span>
                    </Button>
                }
            />

            <PageContent>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="relative w-full sm:max-w-xs">
                        <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                        <Input
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder={t('remote_devices.search_placeholder', 'Search by hostname…')}
                            className="pl-9"
                            aria-label={t('remote_devices.search_placeholder', 'Search by hostname…')}
                        />
                    </div>
                    <div className="text-muted-foreground flex items-center gap-2 text-sm">
                        <span className="bg-success inline-flex h-2 w-2 rounded-full" aria-hidden />
                        <span>
                            {onlineCount} / {devices.length} {t('remote_devices.online_lower', 'online')}
                        </span>
                    </div>
                </div>

                <Card>
                    <CardContent className="p-0">
                        {devices.length === 0 ? (
                            <EmptyState
                                className="border-0 bg-transparent"
                                icon={<MonitorSmartphone />}
                                title={t('remote_devices.empty_title', 'No devices enrolled')}
                                description={t(
                                    'remote_devices.empty_description',
                                    'Devices appear here automatically after the install script runs on them.',
                                )}
                            />
                        ) : visibleDevices.length === 0 ? (
                            <EmptyState
                                className="border-0 bg-transparent"
                                icon={<Search />}
                                title={t('remote_devices.no_results', 'No devices match your search.')}
                            />
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('remote_devices.col_hostname', 'Hostname')}</TableHead>
                                        <TableHead>{t('remote_devices.col_user', 'User')}</TableHead>
                                        <TableHead>{t('remote_devices.col_rustdesk_id', 'RustDesk ID')}</TableHead>
                                        <TableHead>{t('remote_devices.col_status', 'Status')}</TableHead>
                                        <TableHead>{t('remote_devices.col_last_seen', 'Last seen')}</TableHead>
                                        <TableHead className="text-right">{t('remote_devices.col_actions', 'Actions')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {visibleDevices.map((device) => (
                                        <TableRow key={device.id}>
                                            <TableCell className="font-semibold">{device.hostname}</TableCell>
                                            <TableCell>
                                                {editingId === device.id ? (
                                                    <div className="flex items-center gap-1">
                                                        <Input
                                                            value={editValue}
                                                            onChange={(event) => setEditValue(event.target.value)}
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter') {
                                                                    saveName(device);
                                                                } else if (event.key === 'Escape') {
                                                                    cancelEdit();
                                                                }
                                                            }}
                                                            className="h-8 w-44"
                                                            autoFocus
                                                            placeholder={t('remote_devices.rename_placeholder', 'Enter a name')}
                                                            aria-label={t('remote_devices.edit_name', 'Rename')}
                                                        />
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-7 w-7"
                                                            onClick={() => saveName(device)}
                                                            disabled={savingId === device.id || editValue.trim() === ''}
                                                            aria-label={t('remote_devices.save', 'Save')}
                                                            title={t('remote_devices.save', 'Save')}
                                                        >
                                                            <Check className="h-3.5 w-3.5" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-7 w-7"
                                                            onClick={cancelEdit}
                                                            aria-label={t('remote_devices.cancel', 'Cancel')}
                                                            title={t('remote_devices.cancel', 'Cancel')}
                                                        >
                                                            <X className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <div className="group/name flex items-center gap-1.5">
                                                        {device.assigned_user_name ? (
                                                            <span title={device.assigned_user_upn ?? undefined}>{device.assigned_user_name}</span>
                                                        ) : (
                                                            <span className="text-muted-foreground">—</span>
                                                        )}
                                                        {device.name_overridden && (
                                                            <span
                                                                className="text-muted-foreground inline-flex shrink-0"
                                                                title={t(
                                                                    'remote_devices.name_manual',
                                                                    'Manually set — the Intune sync will not change it',
                                                                )}
                                                            >
                                                                <Lock className="h-3 w-3" aria-hidden />
                                                            </span>
                                                        )}
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="text-muted-foreground h-7 w-7"
                                                            onClick={() => startEdit(device)}
                                                            aria-label={t('remote_devices.edit_name', 'Rename')}
                                                            title={t('remote_devices.edit_name', 'Rename')}
                                                        >
                                                            <Pencil className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono text-sm">{device.rustdesk_id}</span>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7"
                                                        onClick={() => copyId(device)}
                                                        aria-label={t('remote_devices.copy_id', 'Copy ID')}
                                                        title={t('remote_devices.copy_id', 'Copy ID')}
                                                    >
                                                        <Copy className="h-3.5 w-3.5" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {device.online ? (
                                                    <Badge className="border-success/30 bg-success-subtle text-success-subtle-foreground">
                                                        {t('remote_devices.status_online', 'Online')}
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-muted-foreground">
                                                        {t('remote_devices.status_offline', 'Offline')}
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground text-sm">{lastSeenLabel(device)}</TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    {intuneSyncEnabled && (
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            className="h-8 w-8"
                                                            onClick={() => syncDevice(device)}
                                                            disabled={syncingId === device.id}
                                                            aria-label={t('remote_devices.sync', 'Sync')}
                                                            title={t('remote_devices.sync_tooltip', 'Sync this device from Intune')}
                                                        >
                                                            <RotateCw className={`h-3.5 w-3.5 ${syncingId === device.id ? 'animate-spin' : ''}`} />
                                                        </Button>
                                                    )}
                                                    {device.online ? (
                                                        <Button asChild size="sm">
                                                            <a href={`rustdesk://connection/new/${device.rustdesk_id}`}>
                                                                {t('remote_devices.connect', 'Connect')}
                                                            </a>
                                                        </Button>
                                                    ) : (
                                                        <Button size="sm" disabled>
                                                            {t('remote_devices.connect', 'Connect')}
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>

                <p className="text-muted-foreground text-xs">
                    {t('remote_devices.online_help_prefix', 'A device is shown as online when it has reported in the last')} {onlineThresholdMinutes}{' '}
                    {t('remote_devices.minutes', 'minutes')}.
                </p>
            </PageContent>
        </AppLayout>
    );
}
